import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button, Spin } from 'antd';
import type { BtlApplication } from '@api/btlApplicationForm/types';
import { isOoCaseEditableStatus } from '@components/CaseSidebar/config/ooStatus';
import { CaseSidebarItemId } from '@components/CaseSidebar/types';
import { FormValidationProvider } from '@hooks/useBtlFormValidation';
import { BtlBaseForm } from '@pages/BtlApplicationFormPage/BtlBaseForm';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { OoCaseFormFooter } from '../OoCaseFormFooter';
import { useOoCaseApplicantIndividualForm } from './useOoCaseApplicantIndividualForm';

interface CaseApplicantIndividualPageProps {
  case?: BtlApplication;
  isCaseLoading: boolean;
  individualId?: string | null;
  isCreateMode?: boolean;
}

function CaseApplicantIndividualPageContent({
  case: caseData,
  isCaseLoading,
  individualId,
  isCreateMode = false,
}: CaseApplicantIndividualPageProps) {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const form = useOoCaseApplicantIndividualForm({
    caseData,
    individualId,
    isCreateMode,
    isCaseLoading,
  });

  const handleBack = () => {
    navigate({ search: `?section=${CaseSidebarItemId.APPLICANTS}` });
  };

  const handleNavigate = (itemId: CaseSidebarItemId) => {
    navigate({ search: `?section=${itemId}` });
  };

  if (form.isLoading) {
    return <Spin size='large' />;
  }

  const individual = individualId ? caseData?.borrower.individuals.find(item => item.id === individualId) : undefined;

  return (
    <>
      <Button type='link' className='mb-2 w-fit px-0' icon={<ArrowLeftOutlined />} onClick={handleBack}>
        {formatMessage({ id: 'btlApplicationForm.APPLICANTS.label' })}
      </Button>
      {isCreateMode ? (
        <h3 className='mb-4 mt-0'>
          {formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.addIndividual.label' })}
        </h3>
      ) : (
        individual && (
          <h3 className='mb-4 mt-0'>
            {individual.title} {individual.firstName} {individual.lastName}
          </h3>
        )
      )}
      <BtlBaseForm
        key={form.formKey}
        {...form.formProps}
        submitter={
          form.isFormEditable
            ? {
                render: submitterProps => (
                  <OoCaseFormFooter
                    {...form.footerProps}
                    isFormEditable
                    onSubmit={() => submitterProps.submit()}
                    onNavigate={handleNavigate}
                  />
                ),
              }
            : false
        }
      />
    </>
  );
}

export function isOoApplicantIndividualFormEditable(caseData?: BtlApplication): boolean {
  return isOoCaseEditableStatus(caseData?.status);
}

export function CaseApplicantIndividualPage(props: CaseApplicantIndividualPageProps) {
  if (!isOoApplicantIndividualFormEditable(props.case)) {
    return <CaseApplicantIndividualPageContent {...props} />;
  }

  return (
    <FormValidationProvider>
      <CaseApplicantIndividualPageContent {...props} />
    </FormValidationProvider>
  );
}
